import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  ArrowLeft, Mic, FileText, ClipboardCheck, Clock, User, Calendar,
  Receipt, X, AlertCircle, RefreshCw, Trash2, ChevronRight, ExternalLink
} from "lucide-react";
import TreatmentNoteEditor from "@/components/TreatmentNoteEditor";
import { CopyButton } from "@/components/ui/copy-button";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };
const STATUS_FLOW = {
  booked: [
    { action: "arrived", label: "Mark as arrived", cls: "bg-amber-50 text-amber-600 hover:bg-amber-100" },
    { action: "cancelled", label: "Cancel", cls: "bg-red-50 text-red-500 hover:bg-red-100", modal: true },
    { action: "no-show", label: "No-show", cls: "bg-[#1A1B1A]/[0.04] text-[#757675] hover:bg-[#1A1B1A]/[0.08]" },
  ],
  arrived: [
    { action: "completed", label: "Complete appointment", cls: "bg-[#205B5A] text-white hover:bg-[#174544]" },
    { action: "cancelled", label: "Cancel", cls: "bg-red-50 text-red-500 hover:bg-red-100", modal: true },
    { action: "no-show", label: "No-show", cls: "bg-[#1A1B1A]/[0.04] text-[#757675] hover:bg-[#1A1B1A]/[0.08]" },
  ],
  completed: [],
};
// Clinics booked before this simplification may still hold the retired front-end states.
STATUS_FLOW.scheduled = STATUS_FLOW.booked;
STATUS_FLOW.confirmed = STATUS_FLOW.booked;
STATUS_FLOW.waiting = STATUS_FLOW.arrived;
STATUS_FLOW["in-session"] = STATUS_FLOW.arrived;
const INVOICE_STATUS_LABEL = { draft: "Draft", unpaid: "Due", sent: "Due", overdue: "Overdue", paid: "Paid", part_paid: "Part paid", cancelled: "Cancelled" };
const STATUS_BADGE = {
  booked: "bg-[#1A1B1A]/[0.05] text-[#757675]", arrived: "bg-amber-50 text-amber-600",
  waiting: "bg-amber-100 text-amber-700", "in-session": "bg-[#205B5A]/10 text-[#205B5A]",
  completed: "bg-[#205B5A]/10 text-[#205B5A]", cancelled: "bg-red-50 text-red-500",
  "no-show": "bg-red-50 text-red-400", invoiced: "bg-blue-50 text-blue-600", paid: "bg-[#205B5A]/10 text-[#205B5A]",
};

export default function AppointmentDetailPage() {
  const { id } = useParams();
  const [appt, setAppt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleForm, setRescheduleForm] = useState({ date: "", start_time: "", end_time: "" });
  const [linkedNote, setLinkedNote] = useState(null);
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const [patientData, setPatientData] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get(`/appointments/${id}`);
      setAppt(data);
      // Check for linked note
      try {
        const noteRes = await api.get(`/clinical/notes/by-appointment/${id}`);
        setLinkedNote(noteRes.data.note);
      } catch {}
      // Check for linked invoice
      try {
        const invRes = await api.get(`/invoices?patient_id=${data.patient_id}&limit=50`);
        const linked = invRes.data.invoices.find(i => i.appointment_id === id);
        setInvoice(linked || null);
      } catch {}
      // Load patient data for note editor
      try {
        const pRes = await api.get(`/patients/${data.patient_id}`);
        setPatientData(pRes.data);
      } catch {}
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  const updateStatus = async (status) => {
    const payload = { status };
    if (status === "cancelled" && cancelReason) payload.cancellation_reason = cancelReason;
    await api.put(`/appointments/${id}`, payload);
    setShowCancel(false);
    load();
  };

  const handleReschedule = async () => {
    if (!rescheduleForm.date || !rescheduleForm.start_time) return;
    // Calc end time
    const dur = appt.duration_minutes || 30;
    const startMin = parseInt(rescheduleForm.start_time.split(":")[0]) * 60 + parseInt(rescheduleForm.start_time.split(":")[1]);
    const endMin = startMin + dur;
    const endTime = `${String(Math.floor(endMin / 60)).padStart(2, "0")}:${String(endMin % 60).padStart(2, "0")}`;
    await api.put(`/appointments/${id}`, { date: rescheduleForm.date, start_time: rescheduleForm.start_time, end_time: endTime });
    setShowReschedule(false);
    load();
  };

  const createInvoice = async () => {
    try {
      const { data } = await api.post(`/appointments/${id}/invoice`);
      setInvoice(data);
    } catch {}
  };

  if (loading) return <div className="p-6 md:p-8"><div className="animate-pulse h-8 bg-[#1A1B1A]/[0.04] rounded-xl w-48" /></div>;
  if (!appt) return <div className="p-6 md:p-8 text-sm text-[#757675]">Appointment not found</div>;

  const actions = STATUS_FLOW[appt.status] || [];
  const isCompleted = appt.status === "completed";
  const needsInvoice = isCompleted && !invoice;

  return (
    <div className="p-6 md:p-8 max-w-4xl" data-testid="appointment-detail-page">
      <Link to="/app/calendar" className="text-sm text-[#757675] hover:text-[#1A1B1A] flex items-center gap-1 mb-4"><ArrowLeft className="w-3.5 h-3.5" /> Calendar</Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-medium text-[#1A1B1A]" style={H}>
            {appt.patient ? `${appt.patient.first_name} ${appt.patient.last_name}` : "Appointment"}
          </h1>
          <p className="text-sm text-[#757675]">{appt.appointment_type_name} · {appt.date} at {appt.start_time} – {appt.end_time}</p>
        </div>
        <span className={`text-xs font-semibold px-3 py-1 rounded-full capitalize ${STATUS_BADGE[appt.status] || ""}`}>{appt.status}</span>
      </div>

      {/* Alerts */}
      {needsInvoice && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6" data-testid="alert-needs-invoice">
          <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <p className="text-xs text-amber-700 flex-1">Appointment completed but not yet invoiced.</p>
          <button onClick={createInvoice} data-testid="create-invoice-btn" className="text-xs font-medium text-amber-700 bg-amber-100 px-3 py-1 rounded-full hover:bg-amber-200">Create invoice</button>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-5 mb-6">
        {/* Details */}
        <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5">
          <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-3">Details</h3>
          <dl className="space-y-2.5 text-sm">
            <div className="flex items-center gap-2 group">
              <Calendar className="w-3.5 h-3.5 text-[#757675]" /><span className="text-[#757675]">Date</span>
              <span className="ml-auto text-[#1A1B1A] font-medium flex items-center gap-1">
                {appt.date}
                <CopyButton value={appt.date} label="Copy date" toastMessage="Date copied" size="xs" showOnHover />
              </span>
            </div>
            <div className="flex items-center gap-2 group">
              <Clock className="w-3.5 h-3.5 text-[#757675]" /><span className="text-[#757675]">Time</span>
              <span className="ml-auto text-[#1A1B1A] font-medium flex items-center gap-1">
                {appt.start_time} – {appt.end_time}
                <CopyButton value={`${appt.date} ${appt.start_time}`} label="Copy date and time" toastMessage="Date and time copied" size="xs" showOnHover />
              </span>
            </div>
            <div className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-[#757675]" /><span className="text-[#757675]">Practitioner</span><span className="ml-auto text-[#1A1B1A] font-medium">{appt.practitioner?.full_name}</span></div>
            <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-[#757675]" /><span className="text-[#757675]">Duration</span><span className="ml-auto text-[#1A1B1A] font-medium">{appt.duration_minutes} min</span></div>
            {appt.price > 0 && <div className="flex items-center gap-2"><Receipt className="w-3.5 h-3.5 text-[#757675]" /><span className="text-[#757675]">Price</span><span className="ml-auto text-[#205B5A] font-semibold">£{appt.price?.toFixed(2)}</span></div>}
          </dl>
        </div>

        {/* Patient */}
        <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5">
          <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-3">Patient</h3>
          {appt.patient && (
            <div>
              <p className="text-sm font-medium text-[#1A1B1A]">{appt.patient.first_name} {appt.patient.last_name}</p>
              <div className="flex items-center gap-1 mt-1 group">
                <p className="text-xs text-[#757675]">{appt.patient.email}</p>
                <CopyButton value={appt.patient.email} label="Copy email" toastMessage="Email copied" size="xs" showOnHover />
              </div>
              <div className="flex items-center gap-1 group">
                <p className="text-xs text-[#757675]">{appt.patient.phone}</p>
                {appt.patient.phone && <CopyButton value={appt.patient.phone} label="Copy phone" toastMessage="Phone copied" size="xs" showOnHover />}
              </div>
              {appt.notes && <p className="text-xs text-[#4A4B4A] mt-3 bg-[#FAF9F6] p-2 rounded-lg">{appt.notes}</p>}
              <Link to={`/app/patients/${appt.patient.id}`} data-testid="open-patient-profile-btn"
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[#205B5A] hover:text-[#174544] bg-[#205B5A]/5 hover:bg-[#205B5A]/10 px-3 py-1.5 rounded-full transition-colors">
                <User className="w-3 h-3" /> Open patient profile <ExternalLink className="w-2.5 h-2.5" />
              </Link>
            </div>
          )}
        </div>

        {/* Invoice / Payment */}
        <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5">
          <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-3">Payment</h3>
          {invoice ? (
            <div>
              <div className="flex items-center gap-1 group">
                <Link to={`/app/invoices/${invoice.id}`} data-testid="view-invoice-btn" className="text-sm font-medium text-[#205B5A] hover:text-[#174544] flex items-center gap-1">View invoice {invoice.invoice_number} <ChevronRight className="w-3 h-3" /></Link>
                <CopyButton value={invoice.invoice_number} label="Copy invoice number" toastMessage="Invoice number copied" size="xs" showOnHover />
              </div>
              <p className="text-lg font-semibold text-[#1A1B1A] mt-1" style={H}>£{invoice.total?.toFixed(2)}</p>
              <span data-testid="invoice-status-badge" className={`text-[10px] font-semibold px-2 py-0.5 rounded-full mt-1 inline-block ${invoice.status === "paid" ? "bg-[#205B5A]/10 text-[#205B5A]" : "bg-amber-50 text-amber-600"}`}>{INVOICE_STATUS_LABEL[invoice.status] || invoice.status}</span>
              {invoice.status !== "paid" && (
                <Link to={`/app/invoices/${invoice.id}`} data-testid="take-payment-btn"
                  className="mt-3 block text-center text-xs font-medium text-white bg-[#205B5A] px-3 py-1.5 rounded-full hover:bg-[#174544]">
                  Take payment
                </Link>
              )}
            </div>
          ) : isCompleted ? (
            <button onClick={createInvoice} data-testid="create-invoice-card-btn" className="w-full text-left">
              <p className="text-sm text-[#757675] mb-2">No invoice created yet</p>
              <span className="text-xs font-medium text-[#205B5A] bg-[#205B5A]/10 px-3 py-1 rounded-full">Create invoice</span>
            </button>
          ) : (
            <p className="text-sm text-[#757675]">Invoice will be available after appointment is completed.</p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5 mb-6">
        <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-3">Actions</h3>
        <div className="flex flex-wrap gap-2">
          {actions.map(a => (
            <button key={a.action} onClick={() => a.modal ? setShowCancel(true) : updateStatus(a.action)} data-testid={`action-${a.action}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${a.cls}`}>{a.label}</button>
          ))}
          {appt.status !== "cancelled" && appt.status !== "completed" && (
            <button onClick={() => { setRescheduleForm({ date: appt.date, start_time: appt.start_time, end_time: appt.end_time }); setShowReschedule(true); }}
              data-testid="reschedule-btn" className="px-4 py-2 rounded-full text-sm font-medium bg-[#FAF9F6] text-[#4A4B4A] hover:bg-[#EBEAE4] transition-colors flex items-center gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" /> Reschedule
            </button>
          )}
          {isCompleted && (
            <button onClick={() => navigate("/app/appointments")} className="px-4 py-2 rounded-full text-sm font-medium bg-[#FAF9F6] text-[#4A4B4A] hover:bg-[#EBEAE4] transition-colors">
              Book follow-up
            </button>
          )}
        </div>
      </div>

      {/* Consultation session — real workflow */}
      <div className="bg-[#205B5A]/[0.03] rounded-2xl border border-[#205B5A]/10 p-6" data-testid="session-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#205B5A]/10 flex items-center justify-center"><Mic className="w-5 h-5 text-[#205B5A]" /></div>
          <div><h3 className="text-base font-medium text-[#1A1B1A]" style={H}>Consultation session</h3><p className="text-xs text-[#757675]">Document this consultation with AI-assisted treatment notes</p></div>
        </div>

        {/* Status indicators */}
        <div className="grid md:grid-cols-3 gap-3 mb-4">
          <div className="bg-white rounded-xl border border-[#1A1B1A]/[0.06] p-3">
            <div className="flex items-center gap-2 mb-1"><Mic className="w-3.5 h-3.5 text-[#205B5A]" /><span className="text-xs font-semibold text-[#1A1B1A]">Recording</span></div>
            <span className={`text-[9px] font-medium px-2 py-0.5 rounded-full ${linkedNote ? "bg-[#205B5A]/10 text-[#205B5A]" : "bg-[#1A1B1A]/5 text-[#757675]"}`}>{linkedNote ? "Available in editor" : "Not started"}</span>
          </div>
          <div className="bg-white rounded-xl border border-[#1A1B1A]/[0.06] p-3">
            <div className="flex items-center gap-2 mb-1"><FileText className="w-3.5 h-3.5 text-[#205B5A]" /><span className="text-xs font-semibold text-[#1A1B1A]">Treatment note</span></div>
            <span className={`text-[9px] font-medium px-2 py-0.5 rounded-full ${linkedNote?.status === "final" ? "bg-[#205B5A]/10 text-[#205B5A]" : linkedNote ? "bg-[#496275]/10 text-[#496275]" : "bg-[#1A1B1A]/5 text-[#757675]"}`}>{linkedNote?.status === "final" ? "Final" : linkedNote ? "Draft" : "Not started"}</span>
          </div>
          <div className="bg-white rounded-xl border border-[#1A1B1A]/[0.06] p-3">
            <div className="flex items-center gap-2 mb-1"><ClipboardCheck className="w-3.5 h-3.5 text-[#205B5A]" /><span className="text-xs font-semibold text-[#1A1B1A]">Status</span></div>
            <span className={`text-[9px] font-medium px-2 py-0.5 rounded-full ${linkedNote?.status === "final" ? "bg-[#205B5A]/10 text-[#205B5A]" : "bg-[#1A1B1A]/5 text-[#757675]"}`}>{linkedNote?.status === "final" ? "Completed" : linkedNote ? "In progress" : "Ready to start"}</span>
          </div>
        </div>

        {/* Primary action - clinical and entirely optional: completing an appointment never
            requires a treatment note. */}
        <button onClick={() => setShowNoteEditor(true)} data-testid="start-session-btn"
          className="bg-[#205B5A] text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#174544] transition-colors shadow-sm flex items-center gap-2">
          <Mic className="w-4 h-4" />
          {linkedNote ? (linkedNote.status === "final" ? "Open treatment note" : "Continue treatment note") : "New treatment note"}
        </button>
      </div>

      {/* Cancel modal */}
      {showCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" data-testid="cancel-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-medium text-[#1A1B1A] mb-1" style={H}>Cancel appointment</h2>
            <p className="text-sm text-[#757675] mb-4">This will cancel the appointment for {appt.patient?.first_name} {appt.patient?.last_name}.</p>
            <div className="mb-4">
              <label className="block text-xs font-medium text-[#757675] mb-1.5">Reason (optional)</label>
              <textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} rows={2} data-testid="cancel-reason"
                className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] resize-none" placeholder="Why is this being cancelled?" />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowCancel(false)} className="px-4 py-2 text-sm text-[#757675]">Keep appointment</button>
              <button onClick={() => updateStatus("cancelled")} data-testid="confirm-cancel-btn" className="bg-red-500 text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-red-600">Confirm cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule modal */}
      {showReschedule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" data-testid="reschedule-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-medium text-[#1A1B1A] mb-4" style={H}>Reschedule</h2>
            <div className="space-y-3 mb-4">
              <div><label className="block text-xs font-medium text-[#757675] mb-1">New date</label>
                <input type="date" value={rescheduleForm.date} onChange={e => setRescheduleForm({...rescheduleForm, date: e.target.value})} data-testid="reschedule-date"
                  className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" /></div>
              <div><label className="block text-xs font-medium text-[#757675] mb-1">New time</label>
                <input type="time" value={rescheduleForm.start_time} onChange={e => setRescheduleForm({...rescheduleForm, start_time: e.target.value})} data-testid="reschedule-time"
                  className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" /></div>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowReschedule(false)} className="px-4 py-2 text-sm text-[#757675]">Cancel</button>
              <button onClick={handleReschedule} data-testid="confirm-reschedule-btn" className="bg-[#205B5A] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#174544]">Reschedule</button>
            </div>
          </div>
        </div>
      )}

      {/* Treatment Note Editor */}
      {showNoteEditor && appt && (
        <TreatmentNoteEditor
          patientId={appt.patient_id}
          patientData={patientData}
          existingNote={linkedNote ? { ...linkedNote, appointment_id: id } : { appointment_id: id, appointment_type: appt.appointment_type_name }}
          onClose={() => setShowNoteEditor(false)}
          onSaved={() => { setShowNoteEditor(false); load(); }}
        />
      )}
    </div>
  );
}
